import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import { gql } from '../../../graphql/client';
import {
  M_INSERT_VIEW,
  M_INSERT_VIEW_VIEW,
} from '../../../graphql/operations';
import type { ChildViewEdge, View } from '../../../types/forge';
import { useEditorStore } from '../store/editorStore';

/**
 * Top-of-sidebar Screens jump-list.
 *
 * Walks the main_view tree and surfaces every `Screen` regardless of
 * how deeply it's nested under Stack/Tab navigators. Each row shows:
 *
 *   - the Screen name (bold)
 *   - a breadcrumb of the navigator chain leading to it
 *     (e.g. "Tabs / Profile Stack")
 *
 * Clicking a row selects the screen via `useEditorStore.selectView`,
 * which the rest of the builder already responds to (ViewTree highlight,
 * PropertyPanel renders, Canvas focuses).
 *
 * Header buttons add new screens / stacks under the *nearest navigator
 * ancestor* of the current selection, falling back to the root navigator.
 * If the root isn't a navigator, the buttons are disabled with a tooltip
 * explaining what to do.
 *
 * The panel is a pure consumer of `appData` (the same blob ViewTree /
 * Canvas / PropertyPanel consume) — no extra round-trips.
 */
export interface AppDataShape {
  FrontendApp_by_pk: { id: string; main_view_id: string | null } | null;
  View: View[];
}

interface ScreenEntry {
  view: View;
  /** Names of the navigator ancestors, root first (excluding the screen itself). */
  breadcrumb: string[];
  /** Closest navigator ancestor — used for `+ Add Screen` from this row. */
  navigatorParentId: string | null;
}

const NAVIGATOR_TYPES = new Set(['StackNavigator', 'TabNavigator']);

export function ScreensPanel({ appData }: { appData: AppDataShape }) {
  const appId = appData.FrontendApp_by_pk?.id;
  const rootId = appData.FrontendApp_by_pk?.main_view_id ?? null;
  const selectedId = useEditorStore((s) => s.selectedViewId);
  const selectView = useEditorStore((s) => s.selectView);
  const queryClient = useQueryClient();

  const viewsById = useMemo(
    () => new Map<string, View>(appData.View.map((v) => [v.id, v])),
    [appData.View],
  );

  // (childId → parentId) — used to compute the breadcrumb-of-the-selection
  // and to walk up to the nearest navigator. With the tree contract there
  // is at most one parent per child; we use the first parent-edge if the
  // schema ever drifts.
  const parentByChild = useMemo(() => {
    const map = new Map<string, string>();
    for (const v of appData.View) {
      for (const edge of v.child_views ?? []) {
        if (!map.has(edge.related_view_id)) {
          map.set(edge.related_view_id, v.id);
        }
      }
    }
    return map;
  }, [appData.View]);

  /* ------------------ Walk the tree to collect screens ------------------ */

  const screens: ScreenEntry[] = useMemo(() => {
    if (!rootId) return [];
    const out: ScreenEntry[] = [];
    const walk = (id: string, breadcrumb: string[], navParentId: string | null) => {
      const v = viewsById.get(id);
      if (!v) return;
      const isScreen = v.Type === 'Screen';
      const isNav = NAVIGATOR_TYPES.has(v.Type);

      if (isScreen) {
        out.push({ view: v, breadcrumb, navigatorParentId: navParentId });
      }

      const sortedEdges = [...(v.child_views ?? [])].sort(
        (a: ChildViewEdge, b: ChildViewEdge) => a.sort - b.sort,
      );
      for (const edge of sortedEdges) {
        const child = viewsById.get(edge.related_view_id);
        if (!child) continue;
        const nextBreadcrumb = isNav ? [...breadcrumb, v.name] : breadcrumb;
        const nextNavParent = isNav ? v.id : navParentId;
        walk(child.id, nextBreadcrumb, nextNavParent);
      }
    };
    walk(rootId, [], null);
    return out;
  }, [rootId, viewsById]);

  /* --------------------- Compute target navigator -------------------- */

  // For the header `+ Add` buttons: find the nearest navigator ancestor
  // of the currently-selected view; if no selection (or selection is
  // outside any navigator), fall back to the root if it's a navigator.
  const targetNavigatorId = useMemo<string | null>(() => {
    const rootView = rootId ? viewsById.get(rootId) : undefined;
    const rootIsNav = rootView ? NAVIGATOR_TYPES.has(rootView.Type) : false;

    let cursor: string | null = selectedId;
    while (cursor) {
      const v = viewsById.get(cursor);
      if (!v) break;
      if (NAVIGATOR_TYPES.has(v.Type)) return v.id;
      cursor = parentByChild.get(cursor) ?? null;
    }
    return rootIsNav ? rootId ?? null : null;
  }, [selectedId, rootId, viewsById, parentByChild]);

  /* ----------------------------- Mutations ----------------------------- */

  const addUnderNavigator = useMutation({
    mutationFn: async ({
      parentId,
      type,
      seedHomeScreen,
    }: {
      parentId: string;
      type: 'Screen' | 'StackNavigator' | 'TabNavigator';
      seedHomeScreen?: boolean;
    }) => {
      const parent = viewsById.get(parentId);
      const edges = parent?.child_views ?? [];
      const nextSort =
        edges.length === 0 ? 0 : Math.max(...edges.map((e) => e.sort)) + 1;
      const baseName = nextChildName(type, edges, viewsById);
      const created = await gql<{ insert_View_one: View }>(M_INSERT_VIEW, {
        appId,
        type,
        name: baseName,
        sort: nextSort,
      });
      const newId = created.insert_View_one.id;
      await gql(M_INSERT_VIEW_VIEW, { parentId, childId: newId, sort: nextSort });
      if (seedHomeScreen) {
        const home = await gql<{ insert_View_one: View }>(M_INSERT_VIEW, {
          appId,
          type: 'Screen',
          name: `${baseName} Home`,
          sort: 0,
        });
        await gql(M_INSERT_VIEW_VIEW, {
          parentId: newId,
          childId: home.insert_View_one.id,
          sort: 0,
        });
        return home.insert_View_one.id; // jump to the inner Home so the user has a screen to edit
      }
      return newId;
    },
    onSuccess(newId) {
      queryClient.invalidateQueries({ queryKey: ['app-data', appId] });
      selectView(newId);
    },
  });

  /* ------------------------------ Render ------------------------------ */

  const hasNavTarget = targetNavigatorId !== null;
  const disabledReason = hasNavTarget
    ? null
    : 'No navigator in the current tree. Add a Stack or Tab navigator from App Settings first.';

  return (
    <section style={s.section}>
      <header style={s.heading}>
        <span style={s.headingText}>Screens</span>
        <span style={s.count}>{screens.length}</span>
      </header>

      <div style={s.actions}>
        <button
          type="button"
          style={hasNavTarget ? s.btnPrimary : s.btnDisabled}
          disabled={!hasNavTarget || addUnderNavigator.isPending}
          onClick={() =>
            targetNavigatorId &&
            addUnderNavigator.mutate({ parentId: targetNavigatorId, type: 'Screen' })
          }
          title={disabledReason ?? 'Add a screen under the nearest navigator'}
        >
          + Screen
        </button>
        <button
          type="button"
          style={hasNavTarget ? s.btnSecondary : s.btnDisabled}
          disabled={!hasNavTarget || addUnderNavigator.isPending}
          onClick={() =>
            targetNavigatorId &&
            addUnderNavigator.mutate({
              parentId: targetNavigatorId,
              type: 'StackNavigator',
              seedHomeScreen: true,
            })
          }
          title={disabledReason ?? 'Add a nested stack with one Home screen'}
        >
          + Stack
        </button>
      </div>

      {disabledReason && (
        <p style={s.empty}>{disabledReason}</p>
      )}

      {!disabledReason && screens.length === 0 && (
        <p style={s.empty}>
          No screens yet. Use “+ Screen” above to add one.
        </p>
      )}

      <ul style={s.list}>
        {screens.map((entry) => {
          const isSelected = entry.view.id === selectedId;
          return (
            <li key={entry.view.id}>
              <button
                type="button"
                onClick={() => selectView(entry.view.id)}
                style={{
                  ...s.row,
                  ...(isSelected ? s.rowSelected : {}),
                }}
                title={
                  entry.breadcrumb.length > 0
                    ? `${entry.breadcrumb.join(' / ')} / ${entry.view.name}`
                    : entry.view.name
                }
              >
                <span style={s.rowName}>{entry.view.name}</span>
                {entry.breadcrumb.length > 0 && (
                  <span style={s.rowCrumb}>
                    {entry.breadcrumb.join(' / ')}
                  </span>
                )}
              </button>
            </li>
          );
        })}
      </ul>

      {addUnderNavigator.isError && (
        <p style={s.errorText}>
          {(addUnderNavigator.error as Error).message}
        </p>
      )}
    </section>
  );
}

/* ------------------------------ Helpers ------------------------------ */

function nextChildName(
  type: string,
  siblings: ChildViewEdge[],
  viewsById: Map<string, { name: string }>,
): string {
  const usedNames = new Set(
    siblings
      .map((e) => viewsById.get(e.related_view_id)?.name)
      .filter((n): n is string => !!n),
  );
  const base =
    type === 'Screen'         ? 'Screen' :
    type === 'StackNavigator' ? 'Stack'  :
    type === 'TabNavigator'   ? 'Tabs'   :
    'View';
  for (let i = 1; i < 1000; i++) {
    const candidate = `${base} ${i}`;
    if (!usedNames.has(candidate)) return candidate;
  }
  return `${base} ${Date.now() % 10000}`;
}

/* ------------------------------- Styles ------------------------------- */

const s: Record<string, React.CSSProperties> = {
  section: {
    padding: 12,
    borderBottom: '1px solid #E2E8F0',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  heading: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 6,
  },
  headingText: {
    fontSize: 11,
    fontWeight: 700,
    color: '#64748B',
    textTransform: 'uppercase' as const,
    letterSpacing: 0.5,
  },
  count: {
    fontSize: 11,
    color: '#94A3B8',
    backgroundColor: '#F1F5F9',
    padding: '0 6px',
    borderRadius: 8,
  },
  actions: { display: 'flex', gap: 6 },
  btnPrimary: {
    flex: 1,
    fontSize: 11,
    fontWeight: 600,
    color: '#fff',
    backgroundColor: '#4F46E5',
    border: 'none',
    borderRadius: 4,
    padding: '6px 8px',
    cursor: 'pointer',
  },
  btnSecondary: {
    flex: 1,
    fontSize: 11,
    fontWeight: 600,
    color: '#4F46E5',
    backgroundColor: '#fff',
    border: '1px solid #C7D2FE',
    borderRadius: 4,
    padding: '6px 8px',
    cursor: 'pointer',
  },
  btnDisabled: {
    flex: 1,
    fontSize: 11,
    fontWeight: 600,
    color: '#94A3B8',
    backgroundColor: '#F1F5F9',
    border: '1px solid #E2E8F0',
    borderRadius: 4,
    padding: '6px 8px',
    cursor: 'not-allowed',
  },
  empty: {
    margin: 0,
    fontSize: 11,
    color: '#94A3B8',
    lineHeight: 1.4,
  },
  list: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  row: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 1,
    width: '100%',
    padding: '6px 8px',
    backgroundColor: 'transparent',
    border: '1px solid transparent',
    borderRadius: 4,
    cursor: 'pointer',
    color: 'inherit',
    textAlign: 'left' as const,
  },
  rowSelected: {
    backgroundColor: '#EEF2FF',
    borderColor: '#C7D2FE',
    color: '#4F46E5',
  },
  rowName: {
    fontSize: 12,
    fontWeight: 600,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: '100%',
  },
  rowCrumb: {
    fontSize: 10,
    color: '#94A3B8',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: '100%',
  },
  errorText: {
    margin: 0,
    fontSize: 11,
    color: '#c00',
  },
};
